/* eslint-disable import/first */
// eslint-disable-next-line no-console
console.log('main.tsx loaded')

import './index.css'

import { datadogRum } from '@datadog/browser-rum'

/**
 * NOTE: It's extremely important that this file not import anything that's
 *  dependent on the results of the userConfig API, otherwise those
 *  dependencies will be instantiated with incorrect values.
 */
import { markAppEntry } from './analytics/moosePerf'
import { client } from './api'
import { APP_LAUNCH_ID_PARAM } from './constants'
import { identifyDatadogUser, initializeDatadog } from './integrations/datadog'

// Anchor the Moose "full load" measurement at the earliest app code, before the userConfig fetch and
// React mount, so `full_load_duration_ms` on `moose_landing_displayed` reflects app boot-to-Landing.
markAppEntry()

const init = async () => {
  try {
    const params = new URLSearchParams(window.location.search)
    const appLaunchId = params.get(APP_LAUNCH_ID_PARAM) ?? ''
    const t = Date.now().valueOf().toString()

    const { data, error, response } = await client.GET('/openapi/userConfig', {
      params: {
        query: { appLaunchId, t },
      },
    })

    if (error || !data) {
      if (
        response?.status === 302 || // 302 and 403 are expected responses
        response?.status === 403
      ) {
        throw new Error('User not authenticated.')
      } else {
        throw new Error(error?.[0]?.msg ?? 'An unexpected error occured.')
      }
    }

    window.REGARD_USER_ID = data.regardUserId
    window.AI_CHAT_ENABLED = data.regardAiChatEnabled
    window.SCRIBE_ENABLED = data.regardScribeEnabled
    window.regardHashedEncounterId = data.regardHashedEncounterId
    window.regardHashedPatientId = data.regardHashedPatientId
    window.REGARD_ONE_LINER_IS_COLLAPSED = data.regardOneLinerIsCollapsed
    window.REVAMP_ENABLED = data.regardRevampEnabled
    window.ATTESTATION_BTN_ENABLED = data.regardAttestationBtnEnabled
    window.DC_SUMMARY_BTN_ENABLED = data.regardDcSummaryBtnEnabled
    window.MOOSE_ENABLED = data.regardMooseEnabled
    window.regardUserGroups = data.regardUserGroups ?? []
    window.regardUserGroupIds = data.regardUserGroupIds ?? []
    window.regardEmr = data.regardEmr

    // Datadog RUM is initialized before this request so boot-time errors are still captured, which means it could not
    // know the user's identity yet. Tag it now.
    //
    // The nullable fields are typed non-null because pydantic v1 does not emit `nullable`, but they are serialized as
    // `null` when unset. Datadog keeps a `null` context value and only drops `undefined`, so normalize them rather
    // than tagging every admin session with nulls.
    identifyDatadogUser({
      userId: data.regardUserId,
      launchId: appLaunchId || undefined,
      appLaunchLogId: data.regardAppLaunchLogId ?? undefined,
      userGroups: data.regardUserGroups ?? [],
      userGroupIds: data.regardUserGroupIds ?? [],
      practitionerId: data.regardPractitionerId ?? undefined,
      practitionerIds: data.regardPractitionerIds ?? [],
      // Unlike the fields above this is `""` rather than null when there is no app launch, so it needs `||`.
      hashedPatientId: data.regardHashedPatientId || undefined,
    })
  } catch (error) {
    // The app still loads below, falling back to its defaults for whatever the config would have set.
    datadogRum.addError(
      new Error('Error getting /openapi/userConfig.', { cause: error }),
    )
  } finally {
    // There are user config values required for the application to properly initialize,
    //  so we wait for them before loading the rest of the app.
    import('./app')
  }
}

// Version gets injected as an environment variable in the bundling process
window.VERSION = import.meta.env.VITE_APP_VERSION

initializeDatadog()
init()

// Keep the Flask session alive by touching the session endpoint every 10 minutes.
// Each request triggers save_session() on the backend, which refreshes the Redis TTL.
// Without this, idle session keys get evicted by Redis under memory pressure (RND-8439).
// TODO RND-8439: Remove once Auth0 JWT auth replaces Flask session cookies.
setInterval(
  () => client.GET('/openapi/validate-session').catch(() => {}),
  10 * 60 * 1000,
)
